import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';

const PublishedPoliciesPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [policies, setPolicies] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadProjects = useCallback(async () => {
    const { data } = await api.get('/api/projects');
    setProjects(data || []);
    if (!selectedProjectId && data && data.length > 0) {
      setSelectedProjectId(String(data[0].id));
    }
  }, [selectedProjectId]);

  const loadPolicies = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    try {
      const query = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const { data } = await api.get(`/api/projects/${selectedProjectId}/published${query}`);
      setPolicies(data || []);
    } catch {
      toast.error('Failed to load published policies');
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, statusFilter]);

  useEffect(() => {
    const run = async () => {
      try {
        await loadProjects();
      } catch {
        toast.error('Failed to load projects');
      }
    };
    run();
  }, [loadProjects]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const handleStatusUpdate = async (policyId, status) => {
    try {
      await api.put(`/api/published/${policyId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      loadPolicies();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <section className="panel panel--spaced">
      <div className="panel-head">
        <h1>Published Policies</h1>
      </div>

      <div className="row-controls">
        <div className="form-group">
          <label htmlFor="project-filter">Project</label>
          <select id="project-filter" value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)}>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="status-filter">Status</label>
          <select id="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="in_review">In review</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {loading && <p className="muted">Loading published policies...</p>}
      {!loading && policies.length === 0 && <p className="muted">No published policies found.</p>}

      {!loading && policies.length > 0 && (
        <div className="published-list">
          {policies.map((policy) => (
            <article className="published-card" key={policy.id}>
              <div>
                <h3>{policy.title}</h3>
                <p className="muted">Version {policy.publish_version} • {policy.status}</p>
                <p className="muted">Created {new Date(policy.created_at).toLocaleString()}</p>
              </div>
              <div className="row-actions">
                <button className="btn btn--sm" onClick={() => navigate(`/projects/${policy.project_id}/versions/${policy.source_version_id}`)}>
                  Open Source Version
                </button>
                <button className="btn btn--sm" onClick={() => handleStatusUpdate(policy.id, 'in_review')}>In Review</button>
                <button className="btn btn--sm btn--primary" onClick={() => handleStatusUpdate(policy.id, 'published')}>Publish</button>
                <button className="btn btn--sm" onClick={() => handleStatusUpdate(policy.id, 'archived')}>Archive</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default PublishedPoliciesPage;
